import { useContext, useEffect, useMemo } from "react";
import { createRoot } from "react-dom/client";
import { useTranslation } from "react-i18next";

import { queryString } from "capitalrx-utilities";

import "./i18n/i18n";
import { getWhitelabel } from "@/api/axios/whitelabel";
import { BasicRoot } from "@/basic";
import { PremiumRoot } from "@/premium";
import { EnrollmentExperienceResponse } from "./api/types";
import { EnrollmentStore } from "./state";
import { EnrollmentContext } from "./state/context";

const Root = (): JSX.Element => {
  return (<EnrollmentStore><EnrollmentSwitch /></EnrollmentStore>);
};

const EnrollmentSwitch = (): JSX.Element => {
  const search = window.location.search;
  const { client } = queryString.getQueryParams(search);
  const [state, dispatch] = useContext(EnrollmentContext);
  const { i18n } = useTranslation();

  useEffect(() => {
    const pathParts = location.pathname.split("/");
    const potentialClientRedirect = pathParts[1];

    if(!client && potentialClientRedirect && !["freedom", "liberty", "404"].includes(potentialClientRedirect)) {
      window.location.href = `${window.location.protocol}//${window.location.host}/?client=${potentialClientRedirect}`;
    } else {
      getWhitelabel(client as string, i18n.language)
        .then((result) => {
          const favicon = (result as EnrollmentExperienceResponse).theme?.favicon;
          if(favicon) {
            setFavicon(favicon);
          }
          dispatch({
            type: "SET_WHITELABEL_DATA",
            payload: result as EnrollmentExperienceResponse,
          });
        })
        .catch((err) => {
          console.log("oops", err);
        })
        .finally(() => {
          dispatch({
            type: "SET_LOADING",
            payload: false,
          });
        });
    }
  }, [client, dispatch, i18n.language]);

  const setFavicon = (faviconUrl: string) => {
    let link: HTMLLinkElement = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = faviconUrl;
  };

  const loadedRender = useMemo(() => {
    return (state.experienceData.is_premium ? <PremiumRoot /> : <BasicRoot />);
  }, [state.experienceData.is_premium]);

  return (
    <>
      {state.isLoading ? <div></div> : loadedRender}
    </>
  );
};

const container = document.getElementById("root");
const root = createRoot(container);

if (root !== null) {
  root.render(<Root />);
} else {
  throw new Error("Unable to locate app root");
}
